import React from 'react';
import { Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Summarize as SummarizeIcon,
  Payments as PaymentsIcon,
  CheckCircle as CheckCircleIcon,
  Reply as ReplyIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import {
  StatsContainer,
  StatItem,
  IconContainer,
  StatInfo,
  StatLabel,
  StatValue,
  getInventoryStatsIconSx,
} from '../../../inventory/InventoryStats/InventoryStats.styles';

export interface PostSalesCounts {
  total: number;
  pendientesDePago: number;
  completadas: number;
  devueltas: number;
  intercambios: number;
}

interface OrdersSummaryBarProps {
  counts: PostSalesCounts;
  loading?: boolean;
}

const METRICS: Array<{
  key: keyof PostSalesCounts;
  label: string;
  icon: React.ReactNode;
  iconVariant: 'sales' | 'warning' | 'inventory' | 'availability';
}> = [
  { key: 'total', label: 'Total ventas', icon: <SummarizeIcon />, iconVariant: 'inventory' },
  { key: 'completadas', label: 'Completadas', icon: <CheckCircleIcon />, iconVariant: 'sales' },
  {
    key: 'pendientesDePago',
    label: 'Pendientes de pago',
    icon: <PaymentsIcon />,
    iconVariant: 'warning',
  },
  { key: 'devueltas', label: 'Devueltas', icon: <ReplyIcon />, iconVariant: 'availability' },
  {
    key: 'intercambios',
    label: 'Piezas intercambiadas',
    icon: <SwapHorizIcon />,
    iconVariant: 'inventory',
  },
];

export const OrdersSummaryBar: React.FC<OrdersSummaryBarProps> = ({ counts, loading = false }) => {
  const theme = useTheme();
  const iconSxMap = getInventoryStatsIconSx(theme);

  if (loading) {
    return (
      <StatsContainer sx={{ marginBottom: 2 }}>
        {METRICS.map((m) => (
          <StatItem key={m.key}>
            <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
            <StatInfo>
              <Skeleton variant="text" width={60} height={24} />
              <Skeleton variant="text" width={40} height={20} />
            </StatInfo>
          </StatItem>
        ))}
      </StatsContainer>
    );
  }

  return (
    <StatsContainer>
      {METRICS.map((m) => (
        <StatItem key={m.key}>
          <IconContainer sx={iconSxMap[m.iconVariant]}>{m.icon}</IconContainer>
          <StatInfo>
            <StatLabel>{m.label}</StatLabel>
            <StatValue>{counts[m.key]}</StatValue>
          </StatInfo>
        </StatItem>
      ))}
    </StatsContainer>
  );
};
